import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Input,
  useDebouncedValue,
} from "@tellescope/ui";
import { useShallowNavbarStore } from "../../../../store";
import { Filter, Mail, MessageCircle, PanelLeft, Smartphone, SquarePen, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { getInboxView } from "../../utils";
import { useInboxSearchParams } from "../../hooks";
import { InboxFilterDialog } from "./InboxFilterDialog";
import { useMemo, useState } from "react";
import { Link } from "../../../../components";
import { useLocation } from "react-router-dom";
import { PAGE_HEADER_SIZE_STRING } from "../../../../constants/header-sizing";
import { useAuth } from "@tellescope/tanstack-query";
import { ChannelIcon } from "./ChannelIcon";
import { PermissionWrapper } from "../../../../components/PermissionWrapper";

export const InboxThreadListHeader = () => {
  const { t } = useTranslation("inbox");
  const location = useLocation();
  const { selectedOrganization } = useAuth();
  const view = getInboxView(location);
  const [filterDialogIsOpen, setFilterDialogIsOpen] = useState(false);
  const [search, setSearch] = useState<string | undefined>("");
  useDebouncedValue(search, 300, {
    onDebounce: () => {
      setInboxQueryParams((prev) => ({
        ...prev,
        search: search || undefined,
      }));
    },
  });
  const [inboxQueryParams, setInboxQueryParams] = useInboxSearchParams();
  const { toggleCollapsed } = useShallowNavbarStore("toggleCollapsed");

  const filterCount = useMemo<number>(() => {
    return Object.entries(inboxQueryParams).filter(([key, value]) => key !== "search" && !!value).length;
  }, [inboxQueryParams]);

  const viewTitle = useMemo(() => {
    switch (view) {
      case "archive":
        return t("Archive");
      case "trash":
        return t("Trash");
      default:
        return t("Inbox");
    }
  }, [t, view]);

  return (
    <>
      <div className={`flex items-center justify-between pl-2 pr-4 py-2 border-b h-[${PAGE_HEADER_SIZE_STRING}]`}>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" className="w-9 h-9 rounded-md" onClick={toggleCollapsed}>
            <PanelLeft className="h-5 w-5" />
          </Button>
          <h2 className="text-xl font-bold">{viewTitle}</h2>
        </div>

        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9"
                  data-trackingid="messaging-open-compose-dropdown-menu"
                >
                  <SquarePen className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <Link to={`/messaging/${view}/chat/new`} keepSearch data-trackingid="messaging-open-compose-chat-link">
                  <DropdownMenuItem>
                    <MessageCircle className="mr-2 h-4 w-4" />
                    {t("Chat")}
                  </DropdownMenuItem>
                </Link>
                <Link
                  to={`/messaging/${view}/email/new`}
                  keepSearch
                  data-trackingid="messaging-open-compose-email-link"
                >
                  <DropdownMenuItem>
                    <Mail className="mr-2 h-4 w-4" />
                    {t("Email")}
                  </DropdownMenuItem>
                </Link>
                <Link to={`/messaging/${view}/sms/new`} keepSearch data-trackingid="messaging-open-compose-sms-link">
                  <DropdownMenuItem>
                    <Smartphone className="mr-2 h-4 w-4" />
                    {t("SMS")}
                  </DropdownMenuItem>
                </Link>
                {selectedOrganization?.hasConnectedZendesk && selectedOrganization?.ticketThreadsEnabled && (
                  <PermissionWrapper model="ticket_threads" action="create">
                    <Link
                      to={`/messaging/${view}/zendesk/new`}
                      keepSearch
                      data-trackingid="messaging-open-compose-zendesk-link"
                    >
                      <DropdownMenuItem>
                        <ChannelIcon channel="Zendesk" className="w-[12px] h-[12px] ml-0.5 mr-2.5" />
                        {t("Zendesk")}
                      </DropdownMenuItem>
                    </Link>
                  </PermissionWrapper>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
      <div className="flex items-center px-4 py-4">
        <Input
          placeholder={t("Search messages, subjects, users...")}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
          }}
          data-trackingid="messaging-search-input"
          className="mr-2"
        />
        {!!inboxQueryParams.search && (
          <Button
            variant="ghost"
            size="sm-icon"
            hoverLabel={t("Clear search")}
            onClick={() => setSearch("")}
            data-testid="messaging-search-clear"
          >
            <X />
          </Button>
        )}
        <Button
          size="icon"
          variant="ghost"
          onClick={() => {
            setFilterDialogIsOpen((prev) => {
              return !prev;
            });
          }}
          className="relative"
          data-trackingid="messaging-filter-dialog-open"
        >
          <Filter />
          {!!filterCount && (
            <div
              className="text-white rounded-full bg-black absolute right-0 top-0 text-xs w-4 h-4"
              data-testid="messaging-filter-badge"
            >
              {filterCount}
            </div>
          )}
        </Button>
        <InboxFilterDialog open={filterDialogIsOpen} setOpen={setFilterDialogIsOpen} />
      </div>
    </>
  );
};
